


/* ==========================================================================
   REPORT RENDERER & AUDIT MODAL ENGINE (/database/reports.js)
   ========================================================================== */

import { escapeHtml, formatPrettyTitle, formatCaseBrandTitle } from './utils.js?v=20260819-audit2';
import { state } from './state.js?v=20260819-audit2';
import { dispatchBuildPayload } from './api.js?v=20260822-audit3';
import { buildHybridShareUrl, encodeStateToShortcode } from './share-state.js?v=20260819-audit2';
import { activateAccessibleLayer, deactivateAccessibleLayer } from './a11y.js?v=20260819-audit2';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';
import { logCurrentBuildActivity } from './telemetry.js?v=20260822-popular2';

let reportRequestSequence = 0;
let latestStartedReportRequestId = 0;
const REPORT_TELEMETRY_SCHEMA_VERSION = 3;
const reportTelemetryKeys = new Set();
const REPORT_TELEMETRY_KEY_LIMIT = 128;

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function boolVerdictToInt(value) {
  return value === true ? 1 : (value === false ? 0 : null);
}

function parseObjectMaybe(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function getReportRequestPayload() {
  return parseObjectMaybe(state.activeAuditRequestPayload) || null;
}

function stripPrivateReportUi(modal) {
  if (!modal) return;
  ['sffReportPayloadContainer', 'sffReportPayloadPre', 'sffReportViewRaw', 'sffViewModeBtn'].forEach(id => {
    const node = document.getElementById(id);
    if (node && modal.contains(node)) node.remove();
  });
  modal.querySelectorAll('.audit-payload-debug-box').forEach(node => node.remove());
  modal.querySelectorAll('button, a, [data-tab]').forEach(node => {
    const text = String(node.textContent || '').trim().toUpperCase();
    const tab = String(node.getAttribute?.('data-tab') || '').trim().toLowerCase();
    if (tab === 'payload' || text.includes('PAYLOAD DEBUG') || text === 'RAW VIEW' || text === 'FORMATTED VIEW' || text.includes('EXPORT .JSON') || text.includes('EXPORT JSON')) {
      node.remove();
    }
  });
}

function resolveReportCompatibility(data) {
  const requestPayload = getReportRequestPayload();
  const localEval = (data?.local_evaluation && typeof data.local_evaluation === 'object')
    ? data.local_evaluation
    : (requestPayload?.local_sff_evaluation || null);
  const engineFits = typeof localEval?.fits === 'boolean' ? localEval.fits : null;
  const remoteFits = typeof data?.is_compatible === 'boolean' ? data.is_compatible : null;
  const unknownCount = Array.isArray(localEval?.unknowns) ? localEval.unknowns.length : 0;
  const warningCount = Array.isArray(localEval?.warns) ? localEval.warns.length : 0;
  const mismatch = engineFits !== null && remoteFits !== null && engineFits !== remoteFits;

  if (engineFits === false) {
    return { state: 'NO_FIT', value: false, source: 'local_sff_engine', engineFits, remoteFits, mismatch, unknownCount, warningCount };
  }
  if (engineFits === true) {
    const needsVerification = unknownCount > 0 || warningCount > 0;
    return {
      state: needsVerification ? 'FIT_VERIFY' : 'FIT',
      value: true,
      source: 'local_sff_engine',
      engineFits,
      remoteFits,
      mismatch,
      unknownCount,
      warningCount
    };
  }
  if (remoteFits !== null) {
    // Compatibility authority never falls back to an LLM/remote-only verdict.
    // Legacy reports without their local request snapshot remain readable, but
    // their boolean can only be surfaced as an advisory historical signal.
    return { state: 'REMOTE_ONLY', value: null, source: 'remote_audit_legacy', engineFits, remoteFits, mismatch, unknownCount, warningCount };
  }
  return { state: 'UNKNOWN', value: null, source: 'unresolved', engineFits, remoteFits, mismatch, unknownCount, warningCount };
}

export const AUDIT_COLOR_MAP = {
  emerald: { text: 'text-emerald', badge: 'badge-emerald', card: 'card-emerald' },
  amber:   { text: 'text-amber',   badge: 'badge-amber',   card: 'card-amber' },
  rose:    { text: 'text-rose',    badge: 'badge-rose',    card: 'card-rose' },
  sky:     { text: 'text-sky',     badge: 'badge-sky',     card: 'card-sky' },
  indigo:  { text: 'text-indigo',  badge: 'badge-indigo',  card: 'card-indigo' },
  zinc:    { text: 'text-[#71717a]',badge: 'badge-zinc',    card: 'card-zinc' }
};

export const AUDIT_SEVERITY_MAP = {
  CRITICAL:    { badge: 'badge-rose font-black', accent: 'accent-rose' },
  HIGH:        { badge: 'badge-amber font-bold',  accent: 'accent-amber' },
  MEDIUM:      { badge: 'badge-sky font-bold',    accent: 'accent-sky' },
  RECOMMENDED: { badge: 'badge-zinc font-medium', accent: 'accent-zinc' },
  PASSED:      { badge: 'badge-emerald',   accent: 'accent-emerald' },
  INFO:        { badge: 'badge-indigo',    accent: 'accent-indigo' }
};

export function invalidateActiveReport() {
  reportRequestSequence++;
  state.activeAuditRequestPayload = null;
  if (state.activeAuditReportData) {
    state.activeAuditReportData = null;
    state.activeAuditTargetCaseIdx = null;
    state.activeAuditReportHash = null;
    window.activeAuditReportData = null;
    window.activeAuditTargetCaseIdx = null;
    window.activeAuditReportHash = null;

    const reportBtnText = document.getElementById('generateReportText');
    if (reportBtnText) {
      if (state.selectedCaseIdx !== null && state.fetchedCasesGlobal && state.fetchedCasesGlobal[state.selectedCaseIdx]) {
        const caseTitle = formatCaseBrandTitle(state.fetchedCasesGlobal[state.selectedCaseIdx].name).substring(0, 15);
        reportBtnText.innerText = `GENERATE REPORT (${caseTitle})`;
      } else {
        reportBtnText.innerText = 'GENERATE REPORT';
      }
    }
  }
}

export async function triggerReportGeneration(dom, mobileCardBtn = null) {
  if (state.selectedCaseIdx === null) return;

  const targetCaseIdx = state.selectedCaseIdx;
  const targetCase = state.fetchedCasesGlobal[targetCaseIdx];
  if (!targetCase) return;

  // Report generation is only meaningful after the authoritative local engine
  // has passed the selected configuration. Never dispatch a remote audit for a
  // deterministic NO_FIT result.
  const selectedEval = state.resultByIdx?.get?.(targetCaseIdx) || null;
  if (selectedEval?.fits !== true) {
    const reportBtnText = document.getElementById('generateReportText');
    if (dom.generateReportBtn) {
      dom.generateReportBtn.disabled = true;
      dom.generateReportBtn.classList.add('disabled-btn');
      dom.generateReportBtn.setAttribute('aria-disabled', 'true');
      dom.generateReportBtn.dataset.reportEligible = 'false';
    }
    if (reportBtnText) reportBtnText.innerText = 'REPORT BLOCKED — NO FIT';
    if (mobileCardBtn) {
      mobileCardBtn.disabled = true;
      mobileCardBtn.classList.add('disabled-btn');
      const mobileLabel = mobileCardBtn.querySelector('span');
      if (mobileLabel) mobileLabel.innerText = 'NO FIT';
    }
    console.warn('[SFF Report] Remote report generation blocked by authoritative local NO_FIT result.', {
      caseIdx: targetCaseIdx,
      caseName: targetCase.name,
      failures: Array.isArray(selectedEval?.fails) ? selectedEval.fails : []
    });
    return;
  }

  const targetCaseName = formatCaseBrandTitle(targetCase.name) || "SFF BUILD";

  if (state.activeAuditReportData && state.activeAuditTargetCaseIdx === targetCaseIdx) {
    openReportModal(state.activeAuditReportData, targetCaseName, state.activeAuditReportHash);
    if (mobileCardBtn) {
      const cardSpan = mobileCardBtn.querySelector('span');
      if (cardSpan) cardSpan.innerText = 'VIEW REPORT';
    }
    return;
  }

  const requestId = ++reportRequestSequence;
  latestStartedReportRequestId = requestId;
  const textSpan = document.getElementById('generateReportText');
  const originalText = textSpan ? textSpan.innerText : 'GENERATE REPORT';

  let mobileSpan = null;
  let originalMobileText = 'GENERATE REPORT';

  if (mobileCardBtn) {
    mobileSpan = mobileCardBtn.querySelector('span');
    if (mobileSpan) {
      originalMobileText = mobileSpan.innerText;
      mobileSpan.innerText = 'GENERATING...';
    }
    mobileCardBtn.disabled = true;
    mobileCardBtn.classList.add('generating-loading');
  }

  if (textSpan) textSpan.innerText = 'GENERATING...';
  if (dom.generateReportBtn) dom.generateReportBtn.disabled = true;

  const restoreCurrentButtonLabel = () => {
    if (!textSpan) return;
    const currentIdx = state.selectedCaseIdx;
    const currentCase = currentIdx !== null ? state.fetchedCasesGlobal[currentIdx] : null;
    const currentEval = currentIdx !== null ? (state.resultByIdx?.get?.(currentIdx) || null) : null;
    if (currentCase) {
      const currentTitle = formatCaseBrandTitle(currentCase.name).substring(0, 15);
      textSpan.innerText = currentEval?.fits !== true
        ? 'REPORT BLOCKED — NO FIT'
        : (state.activeAuditReportData && state.activeAuditTargetCaseIdx === currentIdx
          ? `VIEW REPORT (${currentTitle})`
          : `GENERATE REPORT (${currentTitle})`);
    } else {
      textSpan.innerText = 'GENERATE REPORT';
    }
  };

  try {
    const result = await dispatchBuildPayload(dom, targetCaseIdx);

    // The request belongs to the case/configuration snapshot that started it.
    // If selection/configuration changed while awaiting the API, discard the stale response.
    if (requestId !== reportRequestSequence || state.selectedCaseIdx !== targetCaseIdx) {
      restoreCurrentButtonLabel();
      if (mobileSpan) mobileSpan.innerText = originalMobileText;
      return;
    }

    if (result) {
      state.activeAuditTargetCaseIdx = targetCaseIdx;
      window.activeAuditTargetCaseIdx = targetCaseIdx;
      openReportModal(result, targetCaseName, result.report_id || null);
      // Successful new report generation is stronger intent than a component click.
      // Record exact catalog identities only; reopening an existing cached report above
      // does not receive another popularity credit.
      logCurrentBuildActivity('report');

      if (textSpan) textSpan.innerText = `VIEW REPORT (${targetCaseName.substring(0, 15)})`;
      if (mobileSpan) mobileSpan.innerText = 'VIEW REPORT';
    } else {
      if (textSpan) textSpan.innerText = originalText;
      if (mobileSpan) mobileSpan.innerText = originalMobileText;
    }
  } catch (err) {
    if (requestId === reportRequestSequence) {
      console.error('Report Generation Error:', err);
      if (textSpan) textSpan.innerText = originalText;
      if (mobileSpan) mobileSpan.innerText = originalMobileText;
    }
  } finally {
    // Only the newest started request owns the shared report button state.
    // An invalidated request still cleans itself up; an older request must not
    // re-enable controls while a newer request is actively generating.
    if (latestStartedReportRequestId === requestId) {
      const currentIdx = state.selectedCaseIdx;
      const currentEval = currentIdx !== null ? (state.resultByIdx?.get?.(currentIdx) || null) : null;
      const reportEligible = currentEval?.fits === true;

      if (dom.generateReportBtn) {
        dom.generateReportBtn.disabled = !reportEligible;
        dom.generateReportBtn.classList.toggle('disabled-btn', !reportEligible);
        dom.generateReportBtn.setAttribute('aria-disabled', reportEligible ? 'false' : 'true');
        dom.generateReportBtn.dataset.reportEligible = reportEligible ? 'true' : 'false';
      }

      if (!reportEligible) restoreCurrentButtonLabel();
      latestStartedReportRequestId = 0;
    }
    if (mobileCardBtn) {
      const currentIdx = state.selectedCaseIdx;
      const currentEval = currentIdx !== null ? (state.resultByIdx?.get?.(currentIdx) || null) : null;
      const reportEligible = currentEval?.fits === true;

      mobileCardBtn.disabled = !reportEligible;
      mobileCardBtn.classList.toggle('disabled-btn', !reportEligible);
      mobileCardBtn.classList.remove('generating-loading');
      if (!reportEligible) {
        const mobileLabel = mobileCardBtn.querySelector('span');
        if (mobileLabel) mobileLabel.innerText = 'NO FIT';
      }
    }
  }
}

async function dispatchTelemetryPayload(reportData, targetCaseName) {
  try {
    const metadata = Array.isArray(reportData?.metadata) ? reportData.metadata : [];
    const findMetaVal = (key) => {
      const item = metadata.find(m => {
        const k = String(m?.key || m?.name || '').toLowerCase();
        return k.includes(key.toLowerCase());
      });
      if (!item) return null;
      const num = parseFloat(String(item.value ?? item.val ?? '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(num) ? num : null;
    };

    const activePayload = getReportRequestPayload(reportData);
    // Saved/shared reports no longer persist the private request snapshot. Avoid
    // emitting an incomplete duplicate telemetry event when that private runtime
    // context is intentionally unavailable.
    if (!activePayload) return;
    const targetSpecs = activePayload?.target_case?.normalized_specs || {};
    const buildConfig = activePayload?.build_configuration || {};
    const localEval = activePayload?.local_sff_evaluation || reportData?.local_evaluation || {};
    const effective = localEval?.active_specs || targetSpecs?.effective_clearances || {};
    const effectiveKnown = effective?.known || targetSpecs?.known?.max_clearances || {};
    const graphicsApplicable = buildConfig?.graphics_card?.applicable !== false;
    const airApplicable = buildConfig?.cooling?.air_cooling?.applicable !== false && buildConfig?.cooling?.mode !== 'liquid';
    const compatibility = resolveReportCompatibility(reportData);
    const engineVerdict = boolVerdictToInt(compatibility.engineFits);
    const auditVerdict = boolVerdictToInt(compatibility.remoteFits);
    const legacyVerdict = engineVerdict ?? auditVerdict;

    const telemetryBody = {
      telemetry_schema_version: REPORT_TELEMETRY_SCHEMA_VERSION,
      event_type: 'AUDIT_REPORT_RESULT',
      source: 'remote_audit_report',
      label_source: engineVerdict !== null ? 'deterministic_engine' : (auditVerdict !== null ? 'remote_audit_legacy' : 'unknown'),
      training_label_eligible: false,
      request_contract_version: activePayload?.contract_version ?? reportData?.contract_version ?? null,
      report_id: reportData?.report_id || null,
      timestamp: new Date().toISOString(),
      case_name: targetSpecs?.identity?.name || targetSpecs?.name || targetCaseName || reportData?.case_name || 'SFF Build',
      case_key: activePayload?.target_case?.case_key || targetSpecs?.identity?.case_key || null,
      volume: finiteNumberOrNull(targetSpecs?.volume_l) ?? findMetaVal('volume'),
      max_gpu_len: finiteNumberOrNull(effective?.max_gpu_length_mm)
        ?? finiteNumberOrNull(targetSpecs?.max_clearances?.max_gpu_length_mm)
        ?? findMetaVal('max_gpu'),
      max_gpu_thick: finiteNumberOrNull(effective?.max_gpu_thickness_mm)
        ?? finiteNumberOrNull(targetSpecs?.max_clearances?.max_gpu_thickness_mm),
      max_gpu_height: finiteNumberOrNull(effective?.max_gpu_height_mm)
        ?? finiteNumberOrNull(targetSpecs?.max_clearances?.max_gpu_height_mm),
      max_cpu_height: finiteNumberOrNull(effective?.max_cpu_cooler_height_mm)
        ?? finiteNumberOrNull(targetSpecs?.max_clearances?.max_cpu_cooler_height_mm)
        ?? findMetaVal('max_cpu'),
      max_aio_rad: finiteNumberOrNull(effective?.max_aio_radiator_mm)
        ?? finiteNumberOrNull(targetSpecs?.max_clearances?.max_aio_radiator_mm)
        ?? findMetaVal('aio'),
      max_gpu_known: typeof effectiveKnown?.max_gpu_length_mm === 'boolean' ? effectiveKnown.max_gpu_length_mm : null,
      max_gpu_thick_known: typeof effectiveKnown?.max_gpu_thickness_mm === 'boolean' ? effectiveKnown.max_gpu_thickness_mm : null,
      max_gpu_height_known: typeof effectiveKnown?.max_gpu_height_mm === 'boolean' ? effectiveKnown.max_gpu_height_mm : null,
      max_cpu_known: typeof effectiveKnown?.max_cpu_cooler_height_mm === 'boolean' ? effectiveKnown.max_cpu_cooler_height_mm : null,
      max_aio_known: typeof effectiveKnown?.max_aio_radiator_mm === 'boolean' ? effectiveKnown.max_aio_radiator_mm : null,
      selected_gpu_present: graphicsApplicable,
      selected_gpu_len: graphicsApplicable
        ? (finiteNumberOrNull(buildConfig?.graphics_card?.length_mm) ?? findMetaVal('gpu_len'))
        : null,
      selected_gpu_thick: graphicsApplicable
        ? (finiteNumberOrNull(buildConfig?.graphics_card?.thickness_mm) ?? findMetaVal('gpu_thick'))
        : null,
      selected_gpu_height: graphicsApplicable
        ? (finiteNumberOrNull(buildConfig?.graphics_card?.height_mm) ?? findMetaVal('gpu_height'))
        : null,
      selected_cpu_height: airApplicable
        ? (finiteNumberOrNull(buildConfig?.cooling?.air_cooling?.height_mm) ?? findMetaVal('cpu_height'))
        : null,
      psu_type: buildConfig?.power_supply?.form_factor || null,
      mobo_type: buildConfig?.motherboard?.form_factor || null,
      layout: targetSpecs?.layout || null,
      local_verification_state: localEval?.verification_state || compatibility.state,
      remote_verdict_disagreement: compatibility.mismatch,
      engine_fits_verdict: engineVerdict,
      audit_fits_verdict: auditVerdict,
      // Deprecated analytics alias retained for backend compatibility only.
      fits_verdict: legacyVerdict
    };

    const telemetryKey = [
      telemetryBody.report_id || 'local',
      telemetryBody.case_key || telemetryBody.case_name || '',
      telemetryBody.request_contract_version ?? 'legacy',
      telemetryBody.engine_fits_verdict ?? 'na',
      telemetryBody.audit_fits_verdict ?? 'na'
    ].join('|');
    if (reportTelemetryKeys.has(telemetryKey)) return;

    const response = await fetch('/api/telemetry.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(telemetryBody),
      keepalive: true
    });
    if (!response.ok) throw new Error(`Telemetry HTTP ${response.status}`);

    reportTelemetryKeys.add(telemetryKey);
    while (reportTelemetryKeys.size > REPORT_TELEMETRY_KEY_LIMIT) {
      reportTelemetryKeys.delete(reportTelemetryKeys.values().next().value);
    }
  } catch (err) {
    debugRuntimeIssue('Audit Report Telemetry', err);
  }
}

export class SffAuditRenderer {
  static render(data) {
    if (!data || typeof data !== 'object') {
      return `<div class="audit-markdown-box font-mono">${escapeHtml(String(data || 'No audit data available.'))}</div>`;
    }

    const title = escapeHtml(data.title || 'SFF HARDWARE & SPATIAL CLEARANCE AUDIT');
    const subtitle = escapeHtml(data.subtitle || data.summary || '');
    const versionTag = data.version_tag ? `<span class="audit-pill pill-sky">${escapeHtml(data.version_tag)}</span>` : '';
    const auditType = data.audit_type ? `<span class="audit-pill pill-zinc">${escapeHtml(data.audit_type)}</span>` : '';

    const compatibility = resolveReportCompatibility(data);
    const statusBadge = compatibility.state === 'NO_FIT'
      ? `<span class="audit-status-badge badge-rose">HARDWARE INTERFERENCE DETECTED ⚠</span>`
      : compatibility.state === 'FIT_VERIFY'
        ? `<span class="audit-status-badge badge-amber">FIT · VERIFY ⚠</span>`
        : compatibility.state === 'FIT'
          ? `<span class="audit-status-badge badge-emerald">CLEARANCE VERIFIED ✓</span>`
          : compatibility.state === 'REMOTE_ONLY'
            ? `<span class="audit-status-badge badge-amber">LEGACY REMOTE AUDIT · VERIFY</span>`
            : `<span class="audit-status-badge badge-amber">VERDICT UNAVAILABLE · VERIFY</span>`;

    const authorityMismatchHtml = compatibility.mismatch ? `
      <div class="audit-stack-summary font-mono" style="border-color:rgba(245,158,11,.45);">
        <span class="audit-section-label" style="color:#fbbf24;">AUTHORITY MISMATCH</span>
        <p class="audit-summary-text" style="color:#fbbf24;">Remote audit verdict disagreed with the local deterministic engine. The local engine verdict is retained as compatibility authority.</p>
      </div>` : '';

    const runtimePayload = getReportRequestPayload();
    const localEval = (data?.local_evaluation && typeof data.local_evaluation === 'object')
      ? data.local_evaluation
      : (runtimePayload?.local_sff_evaluation || null);
    const localAuthorityHtml = localEval ? (() => {
      const failCount = Array.isArray(localEval.fails) ? localEval.fails.length : 0;
      const unknownCount = Array.isArray(localEval.unknowns) ? localEval.unknowns.length : 0;
      const advisoryCount = Array.isArray(localEval.advisories) ? localEval.advisories.length : 0;
      const atLimit = Array.isArray(localEval.at_limit) ? localEval.at_limit : [];
      const stateLabel = escapeHtml(localEval.verification_state || localEval.verdict || 'UNVERIFIED');
      const atLimitText = atLimit.length ? ` · AT LIMIT: ${escapeHtml(atLimit.map(formatPrettyTitle).join(', '))}` : '';
      return `
        <div class="audit-stack-summary font-mono">
          <span class="audit-section-label">LOCAL ENGINE AUTHORITY</span>
          <p class="audit-summary-text">${stateLabel} · ${failCount} HARD FAIL${failCount === 1 ? '' : 'S'} · ${unknownCount} UNKNOWN · ${advisoryCount} ADVISOR${advisoryCount === 1 ? 'Y' : 'IES'}${atLimitText}</p>
        </div>`;
    })() : '';

    const intelligence = (data?.local_intelligence_summary && typeof data.local_intelligence_summary === 'object')
      ? data.local_intelligence_summary
      : (runtimePayload?.local_intelligence_snapshot || null);
    const localIntelligenceHtml = intelligence ? (() => {
      const parts = [];
      const edge = intelligence.edge_ai;
      const evidence = intelligence.documented_build_evidence;
      const relational = intelligence.relational_gnn;
      if (edge) {
        const risk = Number.isFinite(Number(edge.residual_risk_score))
          ? `${Math.round(Number(edge.residual_risk_score))}/100${edge.residual_risk_level ? ` ${escapeHtml(edge.residual_risk_level)}` : ''}`
          : (edge.inference_skipped ? 'GATED / NOT REQUIRED' : 'DETERMINISTIC FALLBACK');
        parts.push(`EDGE RISK: ${risk}`);
        if (Number.isFinite(Number(edge.assembly_difficulty_1_to_5))) parts.push(`ASSEMBLY: ${Number(edge.assembly_difficulty_1_to_5)}/5`);
        if (edge.cable_risk_detected) parts.push('CABLE RISK');
      }
      if (evidence) {
        const band = escapeHtml(evidence.band || 'NONE');
        const eligible = Number(evidence.evidence_eligible_builds || 0);
        parts.push(`BUILD EVIDENCE: ${band}${eligible > 0 ? ` (${eligible})` : ''}`);
      }
      if (relational?.available) {
        const maxScore = Number(relational.max_interaction_score);
        parts.push(`RELATIONAL GNN: ${Number.isFinite(maxScore) ? maxScore.toFixed(2) : 'AVAILABLE'}`);
      }
      if (!parts.length) return '';
      return `
        <div class="audit-stack-summary font-mono">
          <span class="audit-section-label">LOCAL INTELLIGENCE · ADVISORY ONLY</span>
          <p class="audit-summary-text">${parts.join(' · ')}</p>
        </div>`;
    })() : '';

    const stackSummaryHtml = data.stack_summary ? `
      <div class="audit-stack-summary font-mono">
        <span class="audit-section-label">BUILD CONFIGURATION & LAYOUT</span>
        <p class="audit-summary-text">${escapeHtml(data.stack_summary)}</p>
      </div>` : '';

    const stackPillsHtml = Array.isArray(data.stack_pills) && data.stack_pills.length > 0 ? `
      <div class="audit-pills-row font-mono">
        ${data.stack_pills.map(p => `<span class="audit-pill pill-blue">${escapeHtml(formatPrettyTitle(p || ''))}</span>`).join('')}
      </div>` : '';

    const metadataHtml = this.renderMetadata(data.metadata);
    const checksHtml = this.renderPassedChecksAndWarnings(data);
    const findingsHtml = this.renderFindings(data.findings, compatibility);

    return `
      <div class="sff-audit-report-container font-mono">
        <div class="audit-header-block">
          <div class="audit-header-top">
            <div>
              <div class="audit-title-row">
                <h4 class="audit-main-title">${title}</h4>
                ${versionTag}
                ${auditType}
              </div>
              ${subtitle ? `<p class="audit-subtitle">${subtitle}</p>` : ''}
            </div>
            <div>${statusBadge}</div>
          </div>
          ${authorityMismatchHtml}
          ${localAuthorityHtml}
          ${localIntelligenceHtml}
          ${stackSummaryHtml}
          ${stackPillsHtml}
        </div>
        ${metadataHtml}
        ${checksHtml}
        <div class="audit-findings-section">
          <span class="audit-section-label">DETAILED HARDWARE FINDINGS & RECOMMENDATIONS</span>
          <div class="audit-findings-grid">${findingsHtml}</div>
        </div>
      </div>`;
  }

  static renderMetadata(metadata) {
    if (!Array.isArray(metadata) || metadata.length === 0) return '';

    const items = metadata.map(m => {
      if (!m) return '';
      const rawKey = String(m.key || m.name || '');
      const rawVal = String(m.value || m.val || '');
      const displayKey = escapeHtml(formatPrettyTitle(rawKey).replace(/\s+RESULT$/i, ''));
      const rawColor = String(m.color || 'zinc').toLowerCase();
      const colorStyle = AUDIT_COLOR_MAP[rawColor] || AUDIT_COLOR_MAP.zinc;
      const valueText = escapeHtml(formatPrettyTitle(rawVal));

      return `
        <div class="audit-card ${colorStyle.card}">
          <span class="audit-card-key">${displayKey}</span>
          <span class="audit-card-val ${colorStyle.text}">${valueText}</span>
        </div>`;
    }).join('');

    return `<div class="audit-metadata-grid font-mono">${items}</div>`;
  }

  static renderPassedChecksAndWarnings(data) {
    const passed = Array.isArray(data.passed_checks) ? data.passed_checks : [];
    const warnings = Array.isArray(data.warnings) ? data.warnings : [];
    const failures = Array.isArray(data.failures) ? data.failures : [];

    if (passed.length === 0 && warnings.length === 0 && failures.length === 0) return '';

    let html = '<div class="audit-checks-box font-mono">';

    if (failures.length > 0) {
      html += `
        <div class="check-group failures font-mono">
          <span class="check-group-title text-rose">CRITICAL FAILURES</span>
          <ul>${failures.map(f => `<li>❌ ${escapeHtml(String(f || ''))}</li>`).join('')}</ul>
        </div>`;
    }

    if (warnings.length > 0) {
      html += `
        <div class="check-group warnings font-mono">
          <span class="check-group-title text-amber">HARDWARE WARNINGS</span>
          <ul>${warnings.map(w => `<li>⚠️ ${escapeHtml(String(w || ''))}</li>`).join('')}</ul>
        </div>`;
    }

    if (passed.length > 0) {
      html += `
        <div class="check-group passes font-mono">
          <span class="check-group-title text-emerald">VERIFIED CLEARANCES</span>
          <ul>${passed.map(p => `<li>✓ ${escapeHtml(String(p || ''))}</li>`).join('')}</ul>
        </div>`;
    }

    html += '</div>';
    return html;
  }

  static renderFindings(findings, compatibility = { state: 'UNKNOWN' }) {
    if (!Array.isArray(findings) || findings.length === 0) {
      if (compatibility.state === 'NO_FIT') {
        return '<div class="audit-markdown-box font-mono" style="color:#f87171;font-size:11px;">The local engine reports an incompatibility, but the remote audit returned no detailed finding payload.</div>';
      }
      if (compatibility.state === 'REMOTE_ONLY') {
        return '<div class="audit-markdown-box font-mono" style="color:#fbbf24;font-size:11px;">This legacy report has a remote verdict but no attached local deterministic evaluation. Compatibility cannot be re-verified from the remote result alone.</div>';
      }
      if (compatibility.state === 'FIT_VERIFY' || compatibility.state === 'UNKNOWN') {
        return '<div class="audit-markdown-box font-mono" style="color:#fbbf24;font-size:11px;">No detailed remote findings were returned. Review the local verification state and unknown/advisory items above.</div>';
      }
      return '<div class="audit-markdown-box font-mono" style="color:#71717a;font-size:11px;">No physical hardware conflicts or clearance issues detected.</div>';
    }

    return findings.map((f, index) => {
      if (!f || typeof f !== 'object') {
        return `<div class="finding-card accent-zinc font-mono"><p class="finding-desc">${escapeHtml(String(f || ''))}</p></div>`;
      }

      const sevKey = String(f.severity || f.level || f.priority || 'INFO').toUpperCase();
      const sevStyle = AUDIT_SEVERITY_MAP[sevKey] || AUDIT_SEVERITY_MAP.INFO;

      const sectionVal = f.section || f.module || f.component;
      const categoryVal = f.category_tag || f.category || f.tag;
      const impactVal = f.impact_badge || f.impact;
      const effortVal = f.effort_rating || f.effort;

      const sectionTag = sectionVal ? `<span class="audit-pill pill-sky">${escapeHtml(formatPrettyTitle(String(sectionVal)))}</span>` : '';
      const catTag = categoryVal ? `<span class="audit-pill pill-zinc">${escapeHtml(formatPrettyTitle(String(categoryVal)))}</span>` : '';
      const impactTag = impactVal ? `<span class="audit-pill pill-amber">${escapeHtml(formatPrettyTitle(String(impactVal)))}</span>` : '';
      const effortTag = effortVal ? `<span class="audit-pill pill-indigo">${escapeHtml(formatPrettyTitle(String(effortVal)))}</span>` : '';

      const rawDefective = String(f.defective_code || f.defective_snippet || '').trim().replace(/\\n/g, '\n');
      const rawRefactored = String(f.refactored_code || f.refactored_snippet || '').trim().replace(/\\n/g, '\n');
      const hasBothCodeBlocks = rawDefective.length > 0 && rawRefactored.length > 0;

      let defectiveBlock = '';
      if (rawDefective.length > 0) {
        defectiveBlock = `
          <div class="audit-code-box defective font-mono">
            <div class="audit-code-header">
              <span class="audit-code-title text-rose">MISCONFIGURED SPEC / HARDWARE CONFLICT</span>
              <button type="button" class="audit-copy-btn">Copy</button>
            </div>
            <pre class="audit-code-pre"><code>${escapeHtml(rawDefective)}</code></pre>
          </div>`;
      }

      let refactoredBlock = '';
      if (rawRefactored.length > 0) {
        refactoredBlock = `
          <div class="audit-code-box refactored font-mono">
            <div class="audit-code-header">
              <span class="audit-code-title text-emerald">RECOMMENDED HARDWARE PATCH</span>
              <button type="button" class="audit-copy-btn">Copy</button>
            </div>
            <pre class="audit-code-pre"><code>${escapeHtml(rawRefactored)}</code></pre>
          </div>`;
      }

      let codeGrid = '';
      if (rawDefective.length > 0 || rawRefactored.length > 0) {
        codeGrid = `
          <div class="audit-code-grid ${hasBothCodeBlocks ? 'dual-grid' : 'single-grid'} font-mono">
            ${defectiveBlock}
            ${refactoredBlock}
          </div>`;
      }

      const titleText = escapeHtml(String(f.title || f.name || 'Finding'));
      const descText = escapeHtml(String(f.description || f.desc || ''));

      return `
        <div class="finding-card ${sevStyle.accent} font-mono">
          <div class="finding-header">
            <div class="finding-tags">
              <span class="finding-index">#${index + 1}</span>
              ${sectionTag}
              ${catTag}
              ${impactTag}
              ${effortTag}
            </div>
            <span class="audit-status-badge ${sevStyle.badge}">${escapeHtml(sevKey)}</span>
          </div>

          <h5 class="finding-title">${titleText}</h5>
          ${descText ? `<p class="finding-desc">${descText}</p>` : ''}

          ${codeGrid}
        </div>`;
    }).join('');
  }

  static bindCopyEvents(container) {
    if (!container || !(container instanceof HTMLElement)) return;

    container.querySelectorAll('.audit-copy-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const snippetBox = btn.closest('.audit-code-box');
        const codeEl = snippetBox?.querySelector('pre code');
        if (!codeEl) return;

        const textToCopy = codeEl.innerText;
        try {
          await navigator.clipboard.writeText(textToCopy);
          btn.innerText = 'Copied';
          btn.classList.add('copied');
          setTimeout(() => {
            btn.innerText = 'Copy';
            btn.classList.remove('copied');
          }, 2000);
        } catch (err) {
          console.error('Failed to copy spec snippet:', err);
        }
      });
    });
  }
}

export function normalizeReportPayload(response) {
  if (!response || typeof response !== 'object') return null;

  const nested = response.report_data && typeof response.report_data === 'object'
    ? response.report_data
    : null;
  const embeddedRequest = parseObjectMaybe(response.request_payload) || parseObjectMaybe(nested?.request_payload);
  if (embeddedRequest) state.activeAuditRequestPayload = embeddedRequest;

  if (nested) {
    const { request_payload: _privateNestedRequest, ...publicNested } = nested;
    const wrapperVerdict = typeof response.is_compatible === 'boolean' ? response.is_compatible : null;
    return {
      ...publicNested,
      report_id: response.report_id || response.id || nested.report_id,
      case_name: response.case_name || nested.case_name,
      created: response.created || nested.created,
      is_compatible: wrapperVerdict ?? (typeof nested.is_compatible === 'boolean' ? nested.is_compatible : null)
    };
  }

  const { request_payload: _privateRequest, ...publicResponse } = response;
  return {
    ...publicResponse,
    is_compatible: typeof response.is_compatible === 'boolean' ? response.is_compatible : null
  };
}

export async function fetchSavedReportFromApi(reportId) {
  if (!reportId) return null;
  const cleanId = String(reportId).replace('#', '').trim();
  try {
    const res = await fetch(`https://db.reviewer.co/api/sff/report?id=${encodeURIComponent(cleanId)}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch report`);
    return await res.json();
  } catch (e) {
    console.error('Failed to fetch report from API:', e);
    return null;
  }
}

export function openReportModal(rawReportData, targetCaseName = "SFF BUILD", reportHash = null) {
  const reportData = normalizeReportPayload(rawReportData);
  if (!reportData) return;


  state.activeAuditReportData = reportData;
  window.activeAuditReportData = reportData;
  state.activeAuditTargetCaseName = targetCaseName;
  window.activeAuditTargetCaseName = targetCaseName;

  dispatchTelemetryPayload(reportData, targetCaseName);

  const modal = document.getElementById('sffReportModal');
  const hashEl = document.getElementById('sffReportHash');
  const formattedView = document.getElementById('sffReportViewFormatted');

  if (!modal) return;
  stripPrivateReportUi(modal);

  const localBuildSpecs = {
    mobo: document.getElementById('moboType')?.value || 'ITX',
    psu: document.getElementById('psuType')?.value || 'SFX',
    vol: document.getElementById('maxVolume')?.value || '10',
    gpuL: document.getElementById('gpuLength')?.value || '304',
    gpuT: document.getElementById('gpuThickness')?.value || '40',
    gpuH: document.getElementById('gpuHeight')?.value || '137',
    pcie: document.getElementById('gpuPcieSlots')?.value || '2',
    cool: document.getElementById('coolingMode')?.value || 'air',
    cpuH: document.getElementById('cpuHeight')?.value || '47',
    rad: document.getElementById('radiatorSize')?.value || '240',
    fan: document.getElementById('radFanType')?.value || 'standard',
    ramH: document.getElementById('ramHeight')?.value || '35',
    ramModules: document.getElementById('ramModules')?.value || '2'
  };
  const localToken = encodeStateToShortcode(localBuildSpecs, targetCaseName);
  const persistedReportId = reportData.report_id || reportHash || null;
  const buildHash = persistedReportId || `BUILD-${(localToken || 'LOCAL').slice(0, 10).toUpperCase()}`;
  state.activeAuditReportHash = String(buildHash).replace('#', '');
  window.activeAuditReportHash = state.activeAuditReportHash;

  if (hashEl) hashEl.innerText = `${String(buildHash).startsWith('#') ? buildHash : '#' + buildHash} (${targetCaseName.substring(0, 20)})`;


  if (formattedView) {
    try {
      formattedView.innerHTML = SffAuditRenderer.render(reportData);
      SffAuditRenderer.bindCopyEvents(formattedView);
    } catch (err) {
      console.error('Renderer error intercepted:', err);
      formattedView.innerHTML = `<div class="audit-markdown-box font-mono" style="padding:16px;color:#f43f5e;">CRITICAL RENDER ERROR: ${escapeHtml(err.message)}</div>`;
    }
    formattedView.classList.remove('hidden');
    formattedView.style.display = 'block';
  }

  modal.classList.add('active');
  modal.style.display = 'flex';
  activateAccessibleLayer(modal, {
    label: `${targetCaseName} compatibility report`,
    initialFocusSelector: '#sffReportModalClose, #sffReportModalCloseBtn, .sff-modal-close, [data-action="close-report"]',
    onClose: closeReportModal
  });
}

export function closeReportModal() {
  const modal = document.getElementById('sffReportModal');
  if (modal) {
    deactivateAccessibleLayer(modal);
    modal.classList.remove('active');
    modal.style.display = 'none';
  }
}

export function initReportModalListeners() {
  const modal = document.getElementById('sffReportModal');
  if (!modal) return;
  // The report modal is a public product surface. Remove legacy developer/raw
  // payload controls even when older PHP markup is still deployed.
  stripPrivateReportUi(modal);

  modal.addEventListener('click', (e) => {
    // 1. Close Modal via 'X', backdrop click, or close button variants
    const closeBtn = e.target.closest(
      '#sffReportModalClose, #sffReportModalCloseBtn, .sff-modal-close, .sff-report-modal-close, .spec-modal-close, .audit-modal-close, .modal-close, .close-btn, [data-action="close-report"], [data-close]'
    );
    
    const targetBtn = e.target.closest('button, a');
    const text = targetBtn ? targetBtn.textContent.trim().toUpperCase() : '';
    const cleanText = text.replace(/\s+/g, '');

    if (
      e.target === modal || 
      closeBtn || 
      (targetBtn && ['CLOSE', '✕', 'X', '×', 'CLOSEREPORT'].includes(cleanText))
    ) {
      e.preventDefault();
      closeReportModal();
      return;
    }

    // 2. Export & Share Actions
    if (targetBtn) {
      if (text.includes('EXPORT .MD') || text.includes('EXPORT MD')) {
        e.preventDefault();
        if (!state.activeAuditReportData) return;
        const report = state.activeAuditReportData;
        const mdContent = String(report.markdown_report || `# ${state.activeAuditTargetCaseName || 'SFF Build'} Clearance Report\n\n${report.summary || 'Audit completed.'}`);
        const blob = new Blob([mdContent], { type: 'text/markdown;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `audit-report-${state.activeAuditReportHash || 'build'}.md`;
        link.click();
      } else if (text.includes('SHARE REPORT')) {
        e.preventDefault();
        const activeCaseName = state.activeAuditTargetCaseName || (state.selectedCaseIdx !== null ? state.fetchedCasesGlobal[state.selectedCaseIdx]?.name : null);
        const dom = {
          moboVal: document.getElementById('moboType'),
          psuVal: document.getElementById('psuType'),
          vol: document.getElementById('maxVolume'),
          gpuL: document.getElementById('gpuLength'),
          gpuT: document.getElementById('gpuThickness'),
          gpuH: document.getElementById('gpuHeight'),
          gpuPcie: document.getElementById('gpuPcieSlots'),
          cooling: document.getElementById('coolingMode'),
          cpuH: document.getElementById('cpuHeight'),
          radSize: document.getElementById('radiatorSize'),
          radFanVal: document.getElementById('radFanType'),
          ramH: document.getElementById('ramHeight'),
          ramModules: document.getElementById('ramModules'),
          search: document.getElementById('caseSearch')
        };

        const specs = {
          mobo: dom.moboVal ? dom.moboVal.value : 'ITX',
          psu: dom.psuVal ? dom.psuVal.value : 'SFX',
          vol: dom.vol ? dom.vol.value : '10',
          gpuL: dom.gpuL ? dom.gpuL.value : '304',
          gpuT: dom.gpuT ? dom.gpuT.value : '40',
          gpuH: dom.gpuH ? dom.gpuH.value : '137',
          pcie: dom.gpuPcie ? dom.gpuPcie.value : '2',
          cool: dom.cooling ? dom.cooling.value : 'air',
          cpuH: dom.cpuH ? dom.cpuH.value : '47',
          rad: dom.radSize ? dom.radSize.value : '240',
          fan: dom.radFanVal ? dom.radFanVal.value : 'standard',
          ramH: dom.ramH ? dom.ramH.value : '35',
          ramModules: dom.ramModules ? dom.ramModules.value : '2',
          ramSearch: document.querySelector('#ramCombobox .combobox-input')?.value?.trim() || undefined,
          gpuSearch: document.querySelector('#gpuCombobox .combobox-input')?.value?.trim() || undefined,
          cpuSearch: document.querySelector('#cpuCoolerCombobox .combobox-input')?.value?.trim() || undefined,
          aioSearch: document.querySelector('#aioCoolerCombobox .combobox-input')?.value?.trim() || undefined,
          moboSearch: document.querySelector('#moboCombobox .combobox-input')?.value?.trim() || undefined,
          report_id: state.activeAuditReportData?.report_id || undefined
        };

        const shareUrl = buildHybridShareUrl(activeCaseName, specs);
        navigator.clipboard.writeText(shareUrl).then(() => {
          const orig = targetBtn.innerText;
          targetBtn.innerText = 'COPIED LINK';
          setTimeout(() => targetBtn.innerText = orig, 2000);
        }).catch(error => debugRuntimeIssue('Report Share Clipboard', error));
      }
    }
  });

}
